import {
  buildDefaultCloudInitYaml,
  type CreateVmFormParams,
  isWindowsVmSource,
} from '../CreateVirtualMachineModal.helpers'

// Image properties the agent needs. `hw_qemu_guest_agent` gives the VM the channel the agent
// talks over (required). `os_require_quiesce` makes snapshots freeze the disk first (optional).
export const QEMU_REQUIRED_PROP = 'hw_qemu_guest_agent'
export const QEMU_OPTIONAL_PROP = 'os_require_quiesce'

/**
 * QGA-FR-003, QGA-FR-004: Where to read the image properties from, per boot source (same rules
 * `isWindowsVmSource` uses for `os_type`). Undefined when nothing is selected yet.
 */
export const getBootSourceImageMetadata = (
  params: CreateVmFormParams,
  getVolumeSnapshotSourceVolume: (volumeSnapshot: any) => any,
): Record<string, any> | undefined => {
  switch (params.sourceType) {
    case 'image':
    case 'newVolume':
    case 'snapshot':
      return params?.image?.[0]
    case 'existingVolume':
      return params?.volume?.[0]?.volume_image_metadata
    case 'volumeSnapshot':
      return getVolumeSnapshotSourceVolume(params?.volumeSnapshot?.[0])?.volume_image_metadata
    case 'installFromIso':
      return params?.installerIso?.[0]
    default:
      return undefined
  }
}

/**
 * QGA-FR-003, QGA-FR-004: True when the property is `yes` (any case). Images carry custom
 * properties under `.properties` and at top level; volume metadata only at top level.
 */
export const isQemuPropertySet = (metadata: Record<string, any> | undefined, key: string) =>
  String(metadata?.properties?.[key] ?? metadata?.[key]).toLowerCase() === 'yes'

/**
 * QGA-FR-006, QGA-FR-009: The auto-install toggle is offered only where cloud-init can install
 * the agent: not on Windows (it uses virtio-win tools) and not on ISO installs.
 */
export const qemuAutoInstallApplies = (
  params: CreateVmFormParams,
  getVolumeSnapshotSourceVolume: (volumeSnapshot: any) => any,
) =>
  params.sourceType !== 'installFromIso' &&
  !isWindowsVmSource(params, getVolumeSnapshotSourceVolume)

/**
 * QGA-FR-007, QGA-FR-008: When the toggle flips, switch the editor between the example
 * Cloud-init with and without the agent lines. Text the user has edited is left alone.
 */
export const cloudInitForAutoInstall = (cloudInitYaml: string, autoInstall: boolean) =>
  cloudInitYaml === buildDefaultCloudInitYaml(!autoInstall)
    ? buildDefaultCloudInitYaml(autoInstall)
    : cloudInitYaml
